import React, { createContext, useContext } from "react";
import { useLocalStorage } from "@/hooks/use-local-storage";
import {
  DateFormatOption,
  DEFAULT_DATE_FORMAT,
  formatDateString,
} from "@/utils/date-format";

/**
 * User preferences stored in localStorage
 */
interface UserPreferences {
  dateFormat: DateFormatOption;
}

/**
 * Default user preferences
 */
const DEFAULT_PREFERENCES: UserPreferences = {
  dateFormat: DEFAULT_DATE_FORMAT,
};

/**
 * Context type for user preferences
 */
interface UserPreferencesContextType {
  preferences: UserPreferences;
  dateFormat: DateFormatOption;
  setDateFormat: (format: DateFormatOption) => void;
  formatDate: (dateString: string) => string;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | null>(
  null
);

/**
 * Hook to access user preferences
 */
export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error(
      "useUserPreferences must be used within UserPreferencesProvider"
    );
  }
  return context;
};

/**
 * Provider component for user preferences
 */
interface UserPreferencesProviderProps {
  children: React.ReactNode;
}

export const UserPreferencesProvider: React.FC<
  UserPreferencesProviderProps
> = ({ children }) => {
  const [preferences, setPreferences] = useLocalStorage<UserPreferences>(
    "userPreferences",
    DEFAULT_PREFERENCES
  );

  const setDateFormat = (format: DateFormatOption) => {
    setPreferences({
      ...preferences,
      dateFormat: format,
    });
  };

  const formatDate = (dateString: string): string => {
    return formatDateString(dateString, preferences.dateFormat);
  };

  return (
    <UserPreferencesContext.Provider
      value={{
        preferences,
        dateFormat: preferences.dateFormat,
        setDateFormat,
        formatDate,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
};
